import { HttpEventType, HttpResponse } from '@angular/common/http';
import { Injectable, InjectionToken, Provider } from '@angular/core';
import { catchError, finalize, map, Observable, Subject, takeUntil } from 'rxjs';
import { ApiDocumentFile, ApiClientDocumentsResponse, FilesQuery, PdfFromHtmlRequest } from '../api-types/document.api';
import { ClientDocumentsResponse, DocumentFile, DocumentFileSubjectType } from '../app-types/document.app';
import {
	transformApiDocumentEntriesToAppDocumentEntries,
	transformApiDocumentFileToAppDocumentFile,
} from '../transformers/document.transform';
import { BaseRequestService } from './base-request.service';

export const UPLOAD_API = new InjectionToken<UploadApi>('UPLOAD_API');

interface UploadResult<T> {
	request: Observable<number | T | undefined>;
	cancel: () => void;
}

/**
 * This service handles file uploads to the server. The upload function returns also the status
 * of the upload so you can handle the progress of the upload in the UI.
 */
@Injectable()
export class UploadApi extends BaseRequestService {
	public constructor() {
		super();
	}

	/**
	 * Upload a file to the server. In return you get a signal.
	 * The signal emits either the current percentage of the upload progress or the result after the upload is successful.
	 * If the upload fails or another status is reported an error is thrown.
	 * @param file The file to upload.
	 * @returns A signal representing the upload progress or completion.
	 */
	public uploadFile(file: File, subjectType: DocumentFileSubjectType, subjectId: string | number) {
		const formData = new FormData();
		formData.append('document', file);

		// Cancellation trigger if you want to abort an upload.
		const cancelSubject = new Subject<void>();

		const observable = this.postObserve(`uploads/filesubjecttype/${subjectType}/subject/${subjectId}`, formData).pipe(
			takeUntil(cancelSubject), // Cancel when cancelSubject emits
			map((event) => {
				if (event.type === HttpEventType.UploadProgress) {
					const percentDone = Math.round((100 * event.loaded) / (event.total || 1));
					return percentDone;
				} else if (event instanceof HttpResponse) {
					const response = event as HttpResponse<ApiDocumentFile>;
					if (!response.body) return;
					return transformApiDocumentFileToAppDocumentFile(response.body!);
				}

				return 0;
			}),
			catchError((err) => {
				this.logger.error(err);
				throw err;
			}),
			finalize(() => cancelSubject.complete()), // Clean up on completion
		);

		const req: UploadResult<DocumentFile> = {
			request: observable,
			// request: of(60),
			cancel: () => cancelSubject.next(),
		};

		return req;
	}

	public async getClientFiles(clientId: number, query?: FilesQuery) {
		const params = query ? this.buildFilesQuery(query) : undefined;
		const response = await this.get<ApiClientDocumentsResponse>(`uploads/clients/${clientId}/files`, params);

		const result: ClientDocumentsResponse = {
			commonEntries: response.commonEntries,
			entries: {
				entities: transformApiDocumentEntriesToAppDocumentEntries(response.entries.entities),
				offset: response.entries.offset,
				range: response.entries.range,
				totalEntities: response.entries.totalEntities,
			},
		};

		return result;
	}

	public async getDocument(documentId: string) {
		const response = await this.get<ApiDocumentFile>(`uploads/${documentId}`);
		return transformApiDocumentFileToAppDocumentFile(response);
	}

	public async pdfFromHtml(body: PdfFromHtmlRequest): Promise<Blob> {
		return await this.post<Blob, PdfFromHtmlRequest>(
			'uploads/pdfFromHtml',
			body,
			{},
			{
				responseType: 'blob' as 'json',
			},
		);
	}

	public async deleteDocument(documentId: string) {
		await this.remove<void>(`uploads/${documentId}`);
	}

	public async getPdfOfContacts(assignmentId: number) {
		return this.get<Blob>(
			`uploads/contactExport/assignments/${assignmentId}`,
			{},
			{
				responseType: 'blob' as 'json',
			},
		);
	}

	public async getPdfOfClient(clientId: number) {
		return this.get<Blob>(
			`uploads/clientExport/clients/${clientId}`,
			{},
			{
				responseType: 'blob' as 'json',
			},
		);
	}

	private buildFilesQuery(query: FilesQuery): Record<string, unknown> {
		const params: Record<string, unknown> = {};
		if (query.offset !== undefined) params['Offset'] = query.offset;
		if (query.range !== undefined) params['Range'] = query.range;
		params['DisabilityReport'] = query.disabilityReport;
		params['DisabilityKind'] = query.disabilityKind;
		params['Report'] = query.report;
		params['Contact'] = query.contact;
		params['Assignment'] = query.assignment;
		return params;
	}
}

export const provideUploadApi: Provider = {
	provide: UPLOAD_API,
	useClass: UploadApi,
};
